import useInputChange from "hooks/useInputChange";
import useServiceCategories from "hooks/userServiceCategories";
import { FormEvent, useEffect, useState } from "react";
import { Blog, BlogCreate } from "types/blog.type";
import Editor, { ContentEditableEvent } from "react-simple-wysiwyg";

interface Props {
  onSubmit: (record: BlogCreate) => void;
  data: Blog | null;
  isLoading?: boolean;
  onCancel: () => void;
}

const inputClass =
  "w-full px-3 py-2 text-sm rounded-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white text-gray-900 placeholder:text-gray-400";

export default function BlogForm({ onSubmit, data, isLoading, onCancel }: Props) {
  const { state, onChange, setState } = useInputChange<BlogCreate>({
    title: "",
    post: "",
    category: "",
  });
  const [html, setHtml] = useState(data?.post || "");

  const { data: categories } = useServiceCategories();

  useEffect(() => {
    if (data) {
      setState(data);
      setHtml(data.post || "");
    }
  }, [data]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    onSubmit({ ...state, post: html });
  };

  function handleChange(e: ContentEditableEvent) {
    setHtml(e.target.value);
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Title <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          className={inputClass}
          placeholder="Enter post title"
          name="title"
          onChange={onChange}
          value={state.title}
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Category <span className="text-red-500">*</span>
        </label>
        <select
          className={inputClass}
          name="category"
          onChange={onChange}
          value={state.category}
          required
        >
          <option value="">Select category</option>
          {categories?.data?.map((category) => (
            <option key={category._id} value={category.name}>
              {category.name}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Post Content <span className="text-red-500">*</span>
        </label>
        <div className="border border-gray-200 rounded-md overflow-hidden">
          <Editor
            value={html}
            onChange={handleChange}
            className="min-h-[400px]"
            style={{ minHeight: "400px" }}
          />
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 rounded-md text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="px-4 py-2 rounded-md text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
        >
          {isLoading ? "Saving…" : data ? "Save Changes" : "Publish Post"}
        </button>
      </div>
    </form>
  );
}
